import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import type { QuotaExceededError } from '@datarecs/data-models-core-api';
import { QuotaPromptService } from '../services/quota-prompt.service';

/** Narrow an arbitrary error body to a `QUOTA_EXCEEDED` payload without trusting its shape. */
function isQuotaExceededBody(body: unknown): body is QuotaExceededError {
    return (
        !!body &&
        typeof body === 'object' &&
        (body as { error_code?: unknown }).error_code === 'QUOTA_EXCEEDED' &&
        typeof (body as { entitlement_key?: unknown }).entitlement_key === 'string' &&
        typeof (body as { limit?: unknown }).limit === 'number' &&
        typeof (body as { current?: unknown }).current === 'number'
    );
}

/**
 * True when an error was already surfaced to the user by {@link quotaErrorInterceptor} — a 403
 * `QUOTA_EXCEEDED` (upgrade modal or transient-quota toast) or a 429 (slow-down toast). A
 * component's own `error:` handler should use this to *skip its generic error toast* for these
 * cases (the spinner still clears as before), so the user sees one calm, specific message instead
 * of that message plus a red "something failed" toast.
 */
export function isQuotaOrRateLimitError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) return false;
    if (error.status === 429) return true;
    return error.status === 403 && isQuotaExceededBody(error.error);
}

/** Parse the `Retry-After` header (delta-seconds form) into a number, or null if absent/unparseable. */
function parseRetryAfter(error: HttpErrorResponse): number | null {
    const header = error.headers?.get('Retry-After');
    if (header) {
        const seconds = Number(header);
        if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds);
    }
    // Fall back to the body's `retry_after` if the server mirrored it there.
    const bodyRetry = (error.error as { retry_after?: unknown } | null)?.retry_after;
    return typeof bodyRetry === 'number' && bodyRetry >= 0 ? Math.ceil(bodyRetry) : null;
}

/**
 * Centralises quota/rate-limit rendering for every request in the app.
 *
 * - **403 with a `QUOTA_EXCEEDED` body** → an upgrade-prompt modal (entitlement label,
 *   current/limit, upgrade CTA), via {@link QuotaPromptService}. This is what makes a rejected
 *   create (job, connection, user, schedule, API key) surface as "you've hit your limit — upgrade"
 *   instead of a raw red error toast. Add it once here and *every* create flow benefits.
 * - **429** → a calm slow-down notice honouring `Retry-After`.
 *
 * The error is always re-thrown so component-level `error:` handlers still see the failed request
 * (e.g. to clear a spinner). They just no longer need to special-case quota/rate-limit messaging.
 */
export const quotaErrorInterceptor: HttpInterceptorFn = (req, next) => {
    const quotaPrompt = inject(QuotaPromptService);

    return next(req).pipe(
        catchError((error: unknown) => {
            if (error instanceof HttpErrorResponse) {
                if (error.status === 403 && isQuotaExceededBody(error.error)) {
                    quotaPrompt.showQuotaExceeded(error.error);
                } else if (error.status === 429) {
                    quotaPrompt.showRateLimited(parseRetryAfter(error));
                }
            }
            return throwError(() => error);
        })
    );
};
